import { Component, Suspense, lazy, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity, AlertTriangle, ArrowRight, Bot, ChevronLeft, ChevronRight, Clock3, Globe2, Layers3,
  LockKeyhole, MessageSquare, Pause, Play, Radar, Satellite, Settings, Shield, ShieldAlert,
  Ship, Square, Plane, Send, Sparkles, Tv, X, Zap,
} from 'lucide-react'
import { apiFetch, fetchLicenseStatus, fetchPublicAdminSettings, fetchWorldviewHistory, fetchWorldviewHistorySnapshot, fetchWorldviewSnapshot } from './api'
import GlobePanelLazy from './GlobePanelLazy'
import LandingHeroGlobe from './LandingHeroGlobe'
import CameraPopup from './CameraPopup'
import WorldviewSelectedItemModal from './WorldviewSelectedItemModal'
import WorldviewTimelineStrip from './WorldviewTimelineStrip'
import WorldviewTelemetryRail from './WorldviewTelemetryRail'
import { apiPath, navigateToAppPath } from './runtime'
import { BRAND } from './branding'
import type { CameraTelemetry, FlyToFn, ViewBounds } from './GlobePanel'
import type { CameraItem, LayerKey, LicenseStatusResponse, NewsFeedConfig, RelatedVideoItem, VisualMode, WorldviewHistoryEntry, WorldviewSnapshot } from './types'

export type TelemetryMeta = {
  confidence?: 'low' | 'medium' | 'high'
  sourceCount?: number
  corroboration?: 'unconfirmed' | 'partial' | 'corroborated'
  sourceClass?: string
  maturity?: 'raw' | 'correlated' | 'assessed' | 'decision'
  freshnessLabel?: string
}

type AppTab = 'worldview' | 'admin' | 'messaging'
type MarketingRoute = 'landing' | 'login'

type AppProps = {
  marketingRoute?: MarketingRoute
}

const AdminPanel = lazy(() => import('./AdminPanel'))
const MessagingView = lazy(() => import('./MessagingView'))

const layerLabels: Record<LayerKey, string> = {
  flights:     'Flights',
  satellites:  'Satellites',
  maritime:    'Maritime',
  jamming:     'GPS Jamming',
  events:      'Events',
  notams:      'Airspace / Borders',
  earthquakes: 'Earthquakes',
  volcanoes:   'Volcanoes',
  tsunami:     'Tsunami',
  news:        'Breaking News',
  cameras:     'Public Cameras',
  trafficFlow: 'Traffic Flow',
}

const initialLayers: Record<LayerKey, boolean> = {
  flights: true, satellites: true, maritime: true, jamming: true,
  events: true, notams: true, earthquakes: true, volcanoes: true,
  tsunami: true, news: true, cameras: true, trafficFlow: true,
}

const sourceProviderMappings = [
  { key: 'opensky', layer: 'flights' as LayerKey, title: 'OpenSky', matchers: ['opensky', 'flight'] },
  { key: 'adsb', layer: 'flights' as LayerKey, title: 'ADS-B / Flight Feed', matchers: ['ads-b', 'air', 'aviation'] },
  { key: 'cameras', layer: 'cameras' as LayerKey, title: 'Public Cameras', matchers: ['camera', 'traffic', 'cctv'] },
  { key: 'news', layer: 'news' as LayerKey, title: 'Live News Inputs', matchers: ['news', 'broadcast', 'rss'] },
] as const

const globalPois = [
  { id: 'usa', key: 'q', label: 'United States', lat: 39.8283, lon: -98.5795, alt: 2200000, note: 'Continental US overview and national command context' },
  { id: 'russia', key: 'w', label: 'Russia', lat: 61.524, lon: 105.3188, alt: 2400000, note: 'Russian Federation strategic overview' },
  { id: 'china', key: 'e', label: 'China', lat: 35.8617, lon: 104.1954, alt: 2200000, note: 'PRC strategic overview' },
  { id: 'uk', key: 'r', label: 'United Kingdom', lat: 55.3781, lon: -3.436, alt: 1200000, note: 'UK political and defense context' },
  { id: 'singapore', key: 't', label: 'Singapore', lat: 1.3521, lon: 103.8198, alt: 800000, note: 'Maritime and finance chokepoint' },
] as const

const regionalPois = [
  { id: 'las-vegas', label: 'Las Vegas', lat: 36.1699, lon: -115.1398, alt: 140000, note: 'Nevada metro / tourism / transport node', bounds: { north: 37.4, south: 35.5, east: -114.4, west: -116.3 } },
  { id: 'reno', label: 'Reno', lat: 39.5296, lon: -119.8138, alt: 140000, note: 'Northern Nevada logistics and metro node', bounds: { north: 40.5, south: 38.8, east: -118.9, west: -120.6 } },
  { id: 'area-51', label: 'Nevada Test Range', lat: 37.235, lon: -115.8111, alt: 180000, note: 'Restricted range / federal test complex', bounds: { north: 38.5, south: 36.3, east: -114.9, west: -116.7 } },
  { id: 'london', label: 'London', lat: 51.5072, lon: -0.1276, alt: 22000, note: 'Financial, diplomatic, and transport hub', bounds: { north: 52.2, south: 51.1, east: 0.4, west: -0.7 } },
  { id: 'dubai', label: 'Dubai', lat: 25.1972, lon: 55.2744, alt: 18000, note: 'Burj Khalifa / Gulf logistics corridor', bounds: { north: 25.5, south: 24.9, east: 55.6, west: 55.0 } },
  { id: 'austin', label: 'Austin', lat: 30.2672, lon: -97.7431, alt: 16000, note: 'Texas civic and transport camera context', bounds: { north: 30.7, south: 30.0, east: -97.4, west: -98.1 } },
] as const

function cls(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ')
}

function sourceDot(state: string) {
  if (state === 'live') return 'source-live'
  if (state === 'delayed') return 'source-delayed'
  return 'source-standby'
}

export function fmtTime(raw: string): string {
  if (!raw) return ''
  if (raw.includes('T') && raw.includes('-')) {
    try {
      const d = new Date(raw)
      if (!isNaN(d.getTime())) {
        return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      }
    } catch { /* fall through */ }
  }
  return raw
}

function inBounds(lat: number, lon: number, bounds: ViewBounds | null): boolean {
  if (!bounds) return true
  const { north, south, east, west } = bounds
  if (lat < south || lat > north) return false
  if (west <= east) return lon >= west && lon <= east
  return lon >= west || lon <= east
}

function buildFeed(snapshot: WorldviewSnapshot | null, liveEvents: Array<{
  id: string
  title: string
  time?: string
  severity?: string
  source?: string
  lat?: number
  lon?: number
  url?: string
  body?: string
  image?: string
  relatedVideos?: RelatedVideoItem[]
}> = []) {
  if (!snapshot) return []
  const items: Array<{
    id: string
    title: string
    body: string
    time: string
    lat: number
    lon: number
    category: string
    severity: string
    url?: string
    image?: string
    relatedVideos?: RelatedVideoItem[]
    telemetry?: TelemetryMeta
  }> = []
  ;(Array.isArray(snapshot.events) ? snapshot.events : []).forEach(e => items.push({ id: e.id, title: e.title, body: e.description, time: e.time, lat: e.lat, lon: e.lon, category: 'Event', severity: e.severity, telemetry: { confidence: 'medium', sourceCount: 2, corroboration: 'partial', sourceClass: 'multi-source', maturity: 'correlated', freshnessLabel: 'live window' } }))
  ;(Array.isArray(snapshot.earthquakes) ? snapshot.earthquakes : []).forEach(e => {
    const magnitude = typeof e.magnitude === 'number' && Number.isFinite(e.magnitude) ? e.magnitude.toFixed(1) : '?.?'
    const depth = typeof e.depth === 'number' && Number.isFinite(e.depth) ? `${e.depth} km` : 'unknown depth'
    items.push({ id: e.id, title: e.title, body: `M${magnitude} — ${e.place} (${depth})`, time: e.time, lat: e.lat, lon: e.lon, category: 'Earthquake', severity: e.severity, telemetry: { confidence: 'high', sourceCount: 1, corroboration: 'corroborated', sourceClass: 'seismic', maturity: 'raw', freshnessLabel: 'recent' } })
  })
  ;(Array.isArray(snapshot.tsunami) ? snapshot.tsunami : []).forEach(t => items.push({ id: t.id, title: t.title, body: `Source: ${t.source}`, time: t.time, lat: t.lat, lon: t.lon, category: 'Tsunami', severity: t.severity, telemetry: { confidence: 'high', sourceCount: 1, corroboration: 'corroborated', sourceClass: 'warning-center', maturity: 'assessed', freshnessLabel: 'active advisory' } }))
  // News — geo-located events get a special GeoEvent category so they stand out
  ;(Array.isArray(snapshot.newsGeoMarkers) ? snapshot.newsGeoMarkers : []).filter(m => m.expiresAt > Date.now()).forEach(m =>
    items.push({
      id: `geoevt-${m.id}`,
      title: m.title,
      body: `📍 ${m.city}${m.country ? ', ' + m.country : ''} · via ${m.source} · ${m.confidence} confidence`,
      time: m.time,
      lat: m.lat,
      lon: m.lon,
      category: 'GeoEvent',
      severity: m.category === 'defense' ? 'high' : m.category === 'regional' ? 'medium' : 'info',
      url: m.url,
      relatedVideos: [],
      telemetry: { confidence: m.confidence, sourceCount: 1, corroboration: 'partial', sourceClass: 'geo-located-news', maturity: 'assessed', freshnessLabel: '24h geo window' },
    })
  )
  ;(Array.isArray(snapshot.news) ? snapshot.news : []).forEach(n => items.push({ id: n.id, title: n.title, body: n.source, time: n.time, lat: n.lat, lon: n.lon, category: 'News', severity: n.tone < -6 ? 'high' : n.tone < -3 ? 'medium' : 'info', url: n.url, image: n.image, relatedVideos: n.relatedVideos ?? [], telemetry: { confidence: n.tone < -6 ? 'high' : 'medium', sourceCount: 1, corroboration: 'unconfirmed', sourceClass: 'media', maturity: 'raw', freshnessLabel: 'news cycle' } }))
  ;(Array.isArray(snapshot.volcanoes) ? snapshot.volcanoes : []).forEach(v => {
    const alertLevel = typeof v.alertLevel === 'string' ? v.alertLevel : 'unknown'
    items.push({ id: v.id, title: `▲ ${v.name} — ${alertLevel.toUpperCase()}`, body: v.activity, time: '', lat: v.lat, lon: v.lon, category: 'Volcano', severity: alertLevel === 'red' ? 'high' : alertLevel === 'orange' ? 'medium' : 'info', telemetry: { confidence: 'high', sourceCount: 1, corroboration: 'corroborated', sourceClass: 'volcanic-monitoring', maturity: 'assessed', freshnessLabel: 'monitoring feed' } })
  })
  ;(Array.isArray(liveEvents) ? liveEvents : []).forEach(event => items.push({
    id: event.id,
    title: event.title,
    body: event.body || event.source || 'Live event update',
    time: event.time || new Date().toISOString(),
    lat: typeof event.lat === 'number' ? event.lat : 0,
    lon: typeof event.lon === 'number' ? event.lon : 0,
    category: 'News',
    severity: event.severity || 'info',
    url: event.url,
    image: event.image,
    relatedVideos: event.relatedVideos ?? [],
    telemetry: { confidence: 'medium', sourceCount: 1, corroboration: 'unconfirmed', sourceClass: 'live-event', maturity: 'raw', freshnessLabel: 'live update' },
  }))
  return items.sort((a, b) => {
    const ta = new Date(a.time).getTime(), tb = new Date(b.time).getTime()
    if (!isNaN(ta) && !isNaN(tb)) return tb - ta
    if (!isNaN(tb)) return 1
    if (!isNaN(ta)) return -1
    return 0
  })
}

const CATEGORY_ICON: Record<string, string> = {
  Event: '⚡', Earthquake: '🔴', Tsunami: '🌊', News: '📰', Volcano: '🌋', GeoEvent: '◉',
}

function navigateTo(path: string) {
  navigateToAppPath(path)
}

function scheduleIdleWork(work: () => void, delay = 0) {
  if (typeof window === 'undefined') return () => undefined
  if ('requestIdleCallback' in window) {
    const handle = window.requestIdleCallback(work, { timeout: 1500 + delay })
    return () => window.cancelIdleCallback(handle)
  }
  const handle = window.setTimeout(work, delay)
  return () => window.clearTimeout(handle)
}

function MarketingLanding() {
  return (
    <div className="brand-shell">
      <div className="brand-grid" />
      <header className="brand-nav">
        <div className="brand-mark">
          <div className="brand-mark-icon">G</div>
          <div>
            <div className="brand-wordmark">GODSEYE</div>
            <div className="brand-submark">Intelligence Command Platform</div>
          </div>
        </div>
        <div className="brand-nav-actions">
          <button className="brand-link-btn" onClick={() => navigateTo('/login')}>Operator login</button>
          <button className="brand-primary-btn" onClick={() => navigateTo('/app')}>Launch console</button>
        </div>
      </header>

      <main className="brand-main">
        <section className="hero-card">
          <div className="hero-copy">
            <div className="brand-kicker">Mission-aware intelligence operations</div>
            <h1>Professional geospatial intelligence, SOC visibility, and mission control in one command surface.</h1>
            <p>
              Godseye brings together global situational awareness, OSINT monitoring, and operator workflows
              in a premium control environment designed for fast decisions under pressure.
            </p>
            <div className="hero-actions">
              <button className="brand-primary-btn large" onClick={() => navigateTo('/login')}>
                Secure operator login <ArrowRight size={16} />
              </button>
              <button className="brand-secondary-btn large" onClick={() => navigateTo('/app')}>
                View live platform
              </button>
            </div>
            <div className="hero-metrics">
              <div className="metric-card">
                <strong>Global</strong>
                <span>Air, maritime, geospatial, hazard, and news intelligence overlays</span>
              </div>
              <div className="metric-card">
                <strong>Real-time</strong>
                <span>Streaming operational awareness for active investigations and response</span>
              </div>
              <div className="metric-card">
                <strong>Operator-grade</strong>
                <span>Built for confident triage, monitoring, and decisive action</span>
              </div>
            </div>
          </div>

          <div className="hero-panel-cluster">
            <div className="hero-display-card hero-display-primary">
              <div className="hero-display-header">
                <span>Live threat picture</span>
                <span>GLOBAL</span>
              </div>
              <div className="hero-threat-legend">
                <span><i className="legend-dot low" />Low risk</span>
                <span><i className="legend-dot medium" />Medium risk</span>
                <span><i className="legend-dot high" />High risk</span>
                <span className="hero-threat-total">REALTIME DATA FLOOD</span>
              </div>
              <div className="hero-globe-orb hero-globe-orb-cesium">
                <div className="hero-globe-ring ring-a" />
                <div className="hero-globe-ring ring-b" />
                <div className="hero-orbit orbit-a" />
                <div className="hero-orbit orbit-b" />
                <LandingHeroGlobe />
              </div>
              <div className="hero-display-stats">
                <div><span>Sources</span><strong>18 Active</strong></div>
                <div><span>Tracked Entities</span><strong>2,146</strong></div>
                <div><span>Critical Alerts</span><strong>07</strong></div>
              </div>
            </div>
            <div className="hero-display-row">
              <div className="hero-mini-card">
                <Shield size={18} />
                <strong>SOC operations</strong>
                <span>Network telemetry, triage queue, and analyst workflow support</span>
              </div>
              <div className="hero-mini-card">
                <Radar size={18} />
                <strong>WorldView</strong>
                <span>Correlated global events, source health, and contextual overlays</span>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-strip">
          <article className="feature-card">
            <Globe2 size={18} />
            <h3>Unified global picture</h3>
            <p>Correlate flights, maritime traffic, satellite passes, hazards, and breaking news into a coherent operating view.</p>
          </article>
          <article className="feature-card">
            <Shield size={18} />
            <h3>OSINT-first</h3>
            <p>Stay focused on global events, geospatial overlays, live sources, and fast operator awareness without extra drag.</p>
          </article>
          <article className="feature-card">
            <LockKeyhole size={18} />
            <h3>Built for control</h3>
            <p>Professional access control, auditability, and policy-driven workflows for sensitive operational environments.</p>
          </article>
        </section>
      </main>
    </div>
  )
}

function DecipherLogo() {
  const [display, setDisplay] = useState('G0D53Y3')

  useEffect(() => {
    const target = 'GODSEYE'
    const scramble = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let frame = 0
    const timer = window.setInterval(() => {
      frame += 1
      const settled = Math.min(target.length, Math.floor(frame / 4))
      const next = target
        .split('')
        .map((char, index) => {
          if (index < settled) return char
          return scramble[Math.floor(Math.random() * scramble.length)]
        })
        .join('')
      setDisplay(next)
      if (settled >= target.length) {
        setDisplay(target)
        window.clearInterval(timer)
      }
    }, 52)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div className="decipher-logo-shell" aria-label="GODSEYE logo">
      <div className="decipher-logo-grid" />
      <div className="decipher-logo-wordmark">
        <span className="decipher-god">{display.slice(0, 4)}</span>
        <span className="decipher-eye">{display.slice(4)}</span>
      </div>
    </div>
  )
}

class ViewErrorBoundary extends Component<{ label: string, children: ReactNode }, { error: Error | null }> {
  constructor(props: { label: string, children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error) {
    console.error(`[${this.props.label}] render failure`, error)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="note-card" style={{ margin: 12, borderColor: 'rgba(255,80,80,0.45)', color: '#ffd3d3' }}>
          <div className="eyebrow">Render Failure</div>
          <strong>{this.props.label}</strong>
          <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>{this.state.error.message}</div>
        </div>
      )
    }
    return this.props.children
  }
}

function MarketingLogin() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [shake, setShake]       = useState(false)

  const triggerShake = () => {
    setShake(true)
    setTimeout(() => setShake(false), 520)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password) { setError('Please enter your username and password.'); triggerShake(); return }
    setLoading(true); setError('')
    try {
      const r = await fetch(apiPath('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password, remember }),
      })
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'Authentication failed. Check your credentials.'); triggerShake(); return }
      const store = remember ? localStorage : sessionStorage
      store.setItem('godseye_token', data.token)
      store.setItem('godseye_user', JSON.stringify(data.user))
      navigateTo('/app')
    } catch {
      setError('Unable to reach authentication server. Please try again.')
      triggerShake()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="brand-shell login-shell">
      <div className="brand-grid" />
      {/* Animated scan line */}
      <div className="login-scanline" />
      <main className="login-layout">
        <section className="login-brand-panel">
          <div className="brand-mark compact">
            <div className="brand-mark-icon">G</div>
            <div>
              <div className="brand-wordmark">GODSEYE</div>
              <div className="brand-submark">Secure Operator Access</div>
            </div>
          </div>
          <div className="brand-kicker">Protected command environment</div>
          <h1>Authenticate into a premium intelligence operations workspace.</h1>
          <p>
            Access global situational awareness, OSINT monitoring, and mission-critical workflows through a clean,
            high-trust operator experience.
          </p>
          <div className="login-side-points">
            <div className="login-side-point"><Shield size={16} /> Role-aware session controls</div>
            <div className="login-side-point"><Radar size={16} /> Live source and alert visibility</div>
            <div className="login-side-point"><Globe2 size={16} /> Unified global operations surface</div>
          </div>
          <div className="login-access-note">
            <LockKeyhole size={13} />
            <span>Default access: <code>admin</code> / <code>admin</code></span>
          </div>
        </section>

        <section className={`login-card${shake ? ' login-card-shake' : ''}`}>
          <div className="login-card-header">
            <div className="login-status-bar">
              <span className="login-status-dot" />
              <span>SECURE CONNECTION ESTABLISHED</span>
            </div>
            <h2>Operator sign-in</h2>
            <p>Enter your credentials to continue into the GODSEYE command console.</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              <span>Username</span>
              <input
                type="text"
                placeholder="operator_id"
                autoComplete="username"
                value={username}
                onChange={e => { setUsername(e.target.value); setError('') }}
                disabled={loading}
              />
            </label>
            <label>
              <span>Password</span>
              <input
                type="password"
                placeholder="••••••••••••"
                autoComplete="current-password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError('') }}
                disabled={loading}
              />
            </label>
            {error && (
              <div className="login-error">
                <AlertTriangle size={13} /> {error}
              </div>
            )}
            <div className="login-form-row">
              <label className="checkbox-row">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                <span>Keep this device trusted</span>
              </label>
            </div>
            <button type="submit" className="brand-primary-btn full" disabled={loading}>
              {loading ? (
                <><span className="login-spinner" /> Authenticating…</>
              ) : (
                <><LockKeyhole size={15} /> Enter secure workspace</>
              )}
            </button>
          </form>

          <div className="login-divider"><span>Platform assurances</span></div>
          <div className="login-assurance-grid">
            <div className="assurance-card">
              <strong>Access control</strong>
              <span>Role-based access, session hardening, and privileged workflow separation.</span>
            </div>
            <div className="assurance-card">
              <strong>Auditability</strong>
              <span>All operator actions are logged, reviewed, and enforced with clear chain-of-custody controls.</span>
            </div>
          </div>
          <div className="login-footer-note">
            GODSEYE Command Platform · Authorized access only
          </div>
        </section>
      </main>
    </div>
  )
}

export default function App({ marketingRoute }: AppProps = {}) {
  const [activeTab, setActiveTab]       = useState<AppTab>('worldview')
  const [layers, setLayers]             = useState(initialLayers)
  const [mode, setMode]                 = useState<VisualMode>('god')
  const [effectStrength, setEffectStrength] = useState(1)
  const [historyMode, setHistoryMode]   = useState<'live' | 'history'>('live')
  const [sceneName, setSceneName] = useState('')
  const [presetName, setPresetName] = useState('')
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null)
  const [editingPresetName, setEditingPresetName] = useState('')
  const [savedScenes, setSavedScenes] = useState<Array<{ id: string; name: string; lat: number; lon: number; alt: number; mode: VisualMode; mapType: string; effectStrength: number; timelineMinute: number; layers: Record<LayerKey, boolean> }>>([])
  const [savedPresets, setSavedPresets] = useState<Array<{ id: string; name: string; mode: VisualMode; mapType: string; activePressureCategory: string; layers: Record<LayerKey, boolean>; visibleSections?: string[] }>>([])
  const [visibleSections, setVisibleSections] = useState<string[]>(['controls', 'feeds', 'scenes', 'media', 'pressure', 'intel-rail'])
  const [defaultPresetId, setDefaultPresetId] = useState<string | null>(null)
  const [timelineMinute, setTimelineMinute] = useState(38)
  const [historyEntries, setHistoryEntries] = useState<WorldviewHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number>(-1)
  const [historyPlaying, setHistoryPlaying] = useState(false)
  const stockTicker = useMemo(() => ([
    { symbol: 'LMT', price: 462.18, change: 1.42, headline: 'Lockheed rises on missile and F-35 sustainment demand', news: ['Pentagon sustainment demand remains elevated.', 'Analysts note backlog resilience across air and missile programs.'] },
    { symbol: 'RTX', price: 108.64, change: -0.38, headline: 'RTX mixed as supply chain normalizes unevenly', news: ['Pratt and missile segments remain closely watched.', 'Street expects gradual margin recovery.'] },
    { symbol: 'NOC', price: 471.92, change: 0.87, headline: 'Northrop gains on strategic systems interest', news: ['B-21 and strategic programs continue to anchor sentiment.', 'Investors watching federal budget posture.'] },
    { symbol: 'PLTR', price: 31.77, change: 2.16, headline: 'Palantir bid on defense AI narrative', news: ['Government AI and fusion use cases remain central.', 'Momentum traders still driving volatility.'] },
    { symbol: 'MAXR', price: 0, change: 0, headline: 'Maxar private market comps remain relevant to geospatial demand', news: ['Commercial EO demand remains strategically important.', 'Satellite analytics comps continue to inform the sector.'] },
  ]), [])
  const [activeStock, setActiveStock] = useState<any | null>(null)
  const [selectedId, setSelectedId]     = useState<string>('e1')
  const [snapshot, setSnapshot]         = useState<WorldviewSnapshot | null>(null)
  const [cameras, setCameras]           = useState<CameraItem[]>([])
  const [activeCamera, setActiveCamera] = useState<CameraItem | null>(null)
  const [error, setError]               = useState<string>('')
  const [viewBounds, setViewBounds]     = useState<ViewBounds | null>(null)
  const [cameraTelemetry, setCameraTelemetry] = useState<CameraTelemetry>({ headingDeg: 0, pitchDeg: -58, rollDeg: 0, altitudeKm: 24000 })
  const [pressureSeries, setPressureSeries] = useState<any | null>(null)
  const [pressureLoading, setPressureLoading] = useState(false)
  const [pressureError, setPressureError] = useState('')
  const pressurePresets = useMemo(() => ([
    { id: 'taiwan-strait', label: 'Taiwan Strait', center: { lat: 24.0, lon: 119.7 }, radiusKm: 900 },
    { id: 'red-sea', label: 'Red Sea', center: { lat: 20.0, lon: 38.0 }, radiusKm: 1200 },
    { id: 'south-china-sea', label: 'South China Sea', center: { lat: 13.5, lon: 114.0 }, radiusKm: 1600 },
    { id: 'eastern-med', label: 'Eastern Mediterranean', center: { lat: 34.5, lon: 33.5 }, radiusKm: 1000 },
  ]), [])
  const [pressureDetail, setPressureDetail] = useState<any | null>(null)
  const [activePressureCategory, setActivePressureCategory] = useState('conflict')
  const [visiblePressureCategories, setVisiblePressureCategories] = useState<string[]>(['conflict', 'energy', 'unrest', 'weather', 'disaster', 'transport'])
  const [pressureRegionMode, setPressureRegionMode] = useState<'viewport' | 'radius'>('viewport')
  const [activePressurePresetId, setActivePressurePresetId] = useState('taiwan-strait')
  const [pressureRadiusKm, setPressureRadiusKm] = useState(1000)
  const [toastDismissed, setToastDismissed] = useState(false)

  // ── Global toast notifications ────────────────────────────────────────────
  type Toast = { id: number; text: string; kind: 'ok' | 'err' | 'info' }
  const [toasts, setToasts] = useState<Toast[]>([])
  const toastIdRef = useRef(0)
  const pushToast = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = ++toastIdRef.current
    setToasts(prev => [...prev.slice(-4), { id, text, kind }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }, [])
  const [newsFeeds, setNewsFeeds]       = useState<NewsFeedConfig[]>([])
  const [mapType, setMapType]           = useState<string>('dark')
  const [showClassification, setShowClassification] = useState(false)
  const [classificationBanner, setClassificationBanner] = useState('UNCLASSIFIED // FOUO')
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(false)
  const [activeFeedItem, setActiveFeedItem] = useState<ReturnType<typeof buildFeed>[number] | null>(null)
  const [activeVideo, setActiveVideo] = useState<RelatedVideoItem | null>(null)
  const [liveEventLog] = useState<Array<{ id: string; title: string; time?: string; severity?: string; source?: string; lat?: number; lon?: number; url?: string; body?: string; image?: string; relatedVideos?: RelatedVideoItem[] }>>([])
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatusResponse | null>(null)
  const [assistantLoading, setAssistantLoading] = useState(false)
  const [assistantInput, setAssistantInput] = useState('')
  const [assistantMessages, setAssistantMessages] = useState<Array<{ role: 'user' | 'assistant'; text: string }>>([
    { role: 'assistant', text: 'GODSEYE Assistant online. I can analyze what is on screen, explain controls, and help with safe disarmed settings.' },
  ])
  const [ollamaStatus, setOllamaStatus] = useState<'unknown' | 'online' | 'offline'>('unknown')
  const [ollamaLoading, setOllamaLoading] = useState(false)
  const [ollamaAnalysis, setOllamaAnalysis] = useState('')
  const [newsDrawersReady, setNewsDrawersReady] = useState(false)
  const [commandPanel, setCommandPanel] = useState<null | 'layers' | 'scenes' | 'media' | 'feeds'>('layers')
  const [selectedNewsFeed, setSelectedNewsFeed] = useState<NewsFeedConfig | null>(null)
  const flyToFn = useRef<FlyToFn | null>(null)

  useEffect(() => { setToastDismissed(false) }, [selectedId])

  useEffect(() => {
    if (marketingRoute || historyMode !== 'live') return
    let active = true
    const load = async () => {
      try {
        const next = await fetchWorldviewSnapshot()
        if (!active) return
        setSnapshot(next); setError('')
      } catch (err) {
        if (!active) return
        setError(err instanceof Error ? err.message : 'Failed to load snapshot')
      }
    }
    void load()
    const timer = window.setInterval(load, 8000)
    return () => { active = false; window.clearInterval(timer) }
  }, [marketingRoute, historyMode])

  useEffect(() => {
    if (marketingRoute) return
    apiFetch('/api/worldview/cameras').then(r => r.json()).then(setCameras).catch(() => {})
    apiFetch('/api/worldview/scenes').then(r => r.json()).then(data => {
      setSavedScenes(Array.isArray(data?.scenes) ? data.scenes : [])
    }).catch(() => {})
    Promise.all([
      apiFetch('/api/worldview/presets').then(r => r.json()).catch(() => ({ presets: [] })),
      apiFetch('/api/worldview/workspace').then(r => r.json()).catch(() => ({ workspace: null })),
    ]).then(([presetData, workspaceData]) => {
      const presets = Array.isArray(presetData?.presets) ? presetData.presets : []
      const workspace = workspaceData?.workspace
      setSavedPresets(presets)
      if (Array.isArray(workspace?.visibleSections)) setVisibleSections(workspace.visibleSections)
      if (workspace?.defaultPresetId) setDefaultPresetId(workspace.defaultPresetId)
      const defaultPreset = workspace?.defaultPresetId ? presets.find((entry: any) => entry.id === workspace.defaultPresetId) : null
      if (defaultPreset) {
        setMode(defaultPreset.mode)
        setMapType(defaultPreset.mapType)
        setActivePressureCategory(defaultPreset.activePressureCategory)
        setLayers(defaultPreset.layers)
        if (Array.isArray(defaultPreset.visibleSections)) setVisibleSections(defaultPreset.visibleSections)
      }
    }).catch(() => {})
  }, [marketingRoute])

  useEffect(() => {
    if (marketingRoute) return
    void fetchLicenseStatus().then(setLicenseStatus).catch(() => undefined)
  }, [marketingRoute])

  useEffect(() => {
    if (marketingRoute) return
    let active = true
    const loadSettings = async () => {
      try {
        const s = await fetchPublicAdminSettings()
        if (!active) return
        if (s.mapType) setMapType(s.mapType)
        if (s.ui) {
          setShowClassification(s.ui.showClassification ?? false)
          if (s.ui.classificationBanner) setClassificationBanner(s.ui.classificationBanner)
          if (s.ui.defaultMode) setMode(s.ui.defaultMode as VisualMode)
          if (s.ui.visualEffectsEnabled === false) setEffectStrength(0)
          else if (typeof s.ui.visualEffectStrength === 'number') setEffectStrength(s.ui.visualEffectStrength)
        }
        setNewsFeeds((s.newsFeeds ?? []).filter(feed => feed.active).slice(0, 3))
      } catch {
        if (active) setNewsFeeds([])
      }
    }
    void loadSettings()
    const timer = window.setInterval(loadSettings, 5 * 60 * 1000)
    return () => { active = false; window.clearInterval(timer) }
  }, [marketingRoute])

  useEffect(() => {
    if (marketingRoute) return
    const check = async () => {
      try {
        const r = await apiFetch('/api/llm/status')
        const data = await r.json()
        setOllamaStatus(data.enabled && data.online ? 'online' : 'offline')
      } catch { setOllamaStatus('offline') }
    }
    void check()
    const t = window.setInterval(check, 30000)
    return () => window.clearInterval(t)
  }, [marketingRoute])

  useEffect(() => {
    if (marketingRoute) return
    let active = true
    const loadHistory = async () => {
      try {
        if (historyMode === 'history') setHistoryLoading(true)
        const entries = await fetchWorldviewHistory()
        if (!active) return
        setHistoryEntries(entries)
        if (historyMode === 'history' && entries.length > 0 && selectedHistoryIndex < 0) {
          setSelectedHistoryIndex(entries.length - 1)
        }
      } catch {
        if (active) setHistoryEntries([])
      } finally {
        if (active && historyMode === 'history') setHistoryLoading(false)
      }
    }
    void loadHistory()
    const timer = window.setInterval(loadHistory, historyMode === 'history' ? 60000 : 180000)
    return () => { active = false; window.clearInterval(timer) }
  }, [marketingRoute, historyMode])

  useEffect(() => {
    if (historyMode !== 'history' || !historyPlaying || historyEntries.length === 0) return
    const timer = window.setInterval(() => {
      setSelectedHistoryIndex(current => {
        if (current <= 0) {
          setHistoryPlaying(false)
          return Math.max(0, current)
        }
        return current - 1
      })
    }, 1800)
    return () => window.clearInterval(timer)
  }, [historyMode, historyPlaying, historyEntries.length])

  useEffect(() => {
    if (marketingRoute || historyMode !== 'history') return
    const entry = historyEntries[selectedHistoryIndex]
    if (!entry) return
    let active = true
    const loadHistoricalSnapshot = async () => {
      try {
        const archived = await fetchWorldviewHistorySnapshot(entry.filename)
        if (!active) return
        try {
          const geoRes = await apiFetch(`/api/news/geo-markers/at?at=${encodeURIComponent(entry.generatedAt)}`)
          if (geoRes.ok) {
            const geoAtTime = await geoRes.json()
            archived.newsGeoMarkers = Array.isArray(geoAtTime) ? geoAtTime : []
          }
        } catch {}
        setSnapshot(archived)
        setError('')
      } catch (err) {
        if (!active) return
        setError(err instanceof Error ? err.message : 'Failed to load historical snapshot')
      }
    }
    void loadHistoricalSnapshot()
    return () => { active = false }
  }, [marketingRoute, historyMode, historyEntries, selectedHistoryIndex])

  const handleViewChange = useCallback((bounds: ViewBounds) => { setViewBounds(bounds) }, [])
  const handleCameraTelemetryChange = useCallback((telemetry: CameraTelemetry) => { setCameraTelemetry(telemetry) }, [])

  useEffect(() => {
    if (marketingRoute || activeTab !== 'worldview') return
    setNewsDrawersReady(false)
    const cancel = scheduleIdleWork(() => setNewsDrawersReady(true), 900)
    return cancel
  }, [marketingRoute, activeTab])

  useEffect(() => {
    if (!viewBounds) return
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        setPressureLoading(true)
        setPressureError('')
        const safePressurePresets = Array.isArray(pressurePresets) ? pressurePresets : []
        const activePreset = safePressurePresets.find(preset => preset.id === activePressurePresetId) || safePressurePresets[0]
        const region = pressureRegionMode === 'radius'
          ? { mode: 'radius', center: activePreset?.center || { lat: (viewBounds.north + viewBounds.south) / 2, lon: (viewBounds.east + viewBounds.west) / 2 }, radiusKm: pressureRadiusKm }
          : { mode: 'viewport', bounds: viewBounds }
        const res = await apiFetch('/api/pressure/series', {
          method: 'POST',
          body: JSON.stringify({
            region,
            bucketSize: 'hour',
            bucketCount: 24,
            categories: Array.isArray(visiblePressureCategories) ? visiblePressureCategories : [],
          }),
          signal: controller.signal as any,
        })
        if (!res.ok) throw new Error(`pressure ${res.status}`)
        setPressureSeries(await res.json())
      } catch (err: any) {
        if (err?.name === 'AbortError') return
        setPressureError(err?.message || 'Pressure series unavailable')
      } finally {
        setPressureLoading(false)
      }
    }, 350)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [viewBounds, visiblePressureCategories, pressureRegionMode, pressureRadiusKm, activePressurePresetId, pressurePresets])
  const handleFlyToReady = useCallback((fn: FlyToFn) => { flyToFn.current = fn }, [])
  const handleSelectFeedItem = useCallback((item: ReturnType<typeof buildFeed>[number]) => {
    setSelectedId(item.id)
    setActiveFeedItem(item)
    flyToFn.current?.(item.lat, item.lon, 1500000)
  }, [])
  const handleToggleLayer = useCallback((key: LayerKey) => {
    setLayers(current => ({ ...current, [key]: !current[key] }))
  }, [])

  const deleteOperatorPreset = useCallback((presetId: string) => {
    setSavedPresets(current => current.filter(preset => preset.id !== presetId))
    if (defaultPresetId === presetId) {
      setDefaultPresetId(null)
      apiFetch('/api/worldview/workspace', { method: 'POST', body: JSON.stringify({ visibleSections, defaultPresetId: null }) }).catch(() => undefined)
    }
    apiFetch(`/api/worldview/presets/${encodeURIComponent(presetId)}`, { method: 'DELETE' }).catch(() => undefined)
    pushToast('Operator preset deleted', 'info')
  }, [defaultPresetId, visibleSections, pushToast])

  const saveOperatorPreset = useCallback(() => {
    const name = presetName.trim() || `Preset ${savedPresets.length + 1}`
    const preset = {
      id: `preset-${Date.now()}`,
      name,
      mode,
      mapType,
      activePressureCategory,
      layers: { ...layers },
      visibleSections: [...visibleSections],
    }
    setSavedPresets(current => [preset, ...current].slice(0, 12))
    setPresetName('')
    apiFetch('/api/worldview/presets', { method: 'POST', body: JSON.stringify(preset) }).catch(() => undefined)
    pushToast(`Operator preset saved: ${name}`, 'ok')
  }, [presetName, savedPresets.length, mode, mapType, activePressureCategory, layers, visibleSections, pushToast])

  const applySavedPreset = useCallback((preset: { id?: string; mode: VisualMode; mapType: string; activePressureCategory: string; layers: Record<LayerKey, boolean>; name: string; visibleSections?: string[] }) => {
    setMode(preset.mode)
    setMapType(preset.mapType)
    setActivePressureCategory(preset.activePressureCategory)
    setLayers(preset.layers)
    if (Array.isArray(preset.visibleSections)) setVisibleSections(preset.visibleSections)
    if (preset.id) setDefaultPresetId(preset.id)
    pushToast(`Operator preset loaded: ${preset.name}`, 'info')
  }, [pushToast])

  const renameOperatorPreset = useCallback((presetId: string, nextName: string) => {
    const trimmed = nextName.trim()
    if (!trimmed) {
      pushToast('Preset name cannot be empty', 'warn')
      return
    }
    let changed = false
    setSavedPresets(current => current.map(preset => {
      if (preset.id !== presetId) return preset
      if (preset.name === trimmed) return preset
      changed = true
      return { ...preset, name: trimmed }
    }))
    if (!changed) return
    const target = savedPresets.find(preset => preset.id === presetId)
    if (target) {
      apiFetch('/api/worldview/presets', {
        method: 'POST',
        body: JSON.stringify({ ...target, name: trimmed }),
      }).catch(() => undefined)
      pushToast(`Preset renamed: ${trimmed}`, 'info')
    }
    setEditingPresetId(null)
    setEditingPresetName('')
  }, [savedPresets, pushToast])

  const adaptivePois = useMemo(() => {
    if (!viewBounds) return globalPois
    const latSpan = Math.abs(viewBounds.north - viewBounds.south)
    const lonSpan = Math.abs(viewBounds.east - viewBounds.west)
    const isGlobalView = cameraTelemetry.altitudeKm > 3500 || latSpan > 45 || lonSpan > 60
    if (isGlobalView) return globalPois
    return regionalPois.filter(poi => {
      const bounds = poi.bounds
      return !(bounds.west > viewBounds.east || bounds.east < viewBounds.west || bounds.south > viewBounds.north || bounds.north < viewBounds.south)
    }).slice(0, 5)
  }, [viewBounds, cameraTelemetry.altitudeKm])

  const applySourcePreset = useCallback((preset: 'air-picture' | 'urban-traffic' | 'news-narrative' | 'full-fusion') => {
    if (preset === 'air-picture') {
      setLayers(current => ({ ...current, flights: true, satellites: true, maritime: false, jamming: true, events: true, notams: true, earthquakes: false, volcanoes: false, tsunami: false, news: false, cameras: false, trafficFlow: false }))
      setMode('radar')
      setMapType('dark')
      setActivePressureCategory('conflict')
      pushToast('Source preset applied: Air picture', 'info')
      return
    }
    if (preset === 'urban-traffic') {
      setLayers(current => ({ ...current, flights: false, satellites: false, maritime: false, jamming: false, events: true, notams: false, earthquakes: false, volcanoes: false, tsunami: false, news: true, cameras: true, trafficFlow: true }))
      setMode('photoreal')
      setMapType('osm')
      setActivePressureCategory('transport')
      pushToast('Source preset applied: Urban traffic', 'info')
      return
    }
    if (preset === 'news-narrative') {
      setLayers(current => ({ ...current, flights: false, satellites: false, maritime: false, jamming: false, events: true, notams: false, earthquakes: true, volcanoes: true, tsunami: true, news: true, cameras: false, trafficFlow: false }))
      setMode('crt')
      setMapType('dark')
      setActivePressureCategory('unrest')
      pushToast('Source preset applied: News narrative', 'info')
      return
    }
    setLayers(current => ({ ...current, flights: true, satellites: true, maritime: true, jamming: true, events: true, notams: true, earthquakes: true, volcanoes: true, tsunami: true, news: true, cameras: true, trafficFlow: true }))
    setMode('god')
    setMapType('dark')
    setActivePressureCategory('conflict')
    pushToast('Source preset applied: Full fusion', 'ok')
  }, [pushToast])

  // ── Global keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    if (marketingRoute) return
    const handler = (e: KeyboardEvent) => {
      // Ignore when focus is inside an input/textarea/select
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      switch (e.key) {
        case 'Escape':
          // Close any open overlay in priority order
          if (activeCamera)       { setActiveCamera(null); break }
          if (activeFeedItem)     { setActiveFeedItem(null); break }
          if (activeVideo)        { setActiveVideo(null); break }
          break
        case '1': if (!e.metaKey && !e.ctrlKey) setActiveTab('worldview'); break
        case '2': if (!e.metaKey && !e.ctrlKey) setActiveTab('messaging'); break
        case '3': if (!e.metaKey && !e.ctrlKey) setActiveTab('admin'); break
        case 'l': case 'L':
          if (!e.metaKey && !e.ctrlKey) setLeftRailCollapsed(v => !v)
          break
        case 'h': case 'H':
          if (!e.metaKey && !e.ctrlKey) {
            setHistoryMode(m => m === 'live' ? 'history' : 'live')
            if (historyMode === 'live' && Array.isArray(historyEntries) && historyEntries.length > 0 && selectedHistoryIndex < 0)
              setSelectedHistoryIndex(0)
          }
          break
        case 'ArrowLeft':
          if (historyMode === 'history' && Array.isArray(historyEntries) && historyEntries.length > 0)
            setSelectedHistoryIndex(i => Math.min(historyEntries.length - 1, Math.max(0, i + 1)))
          break
        case 'ArrowRight':
          if (historyMode === 'history' && Array.isArray(historyEntries) && historyEntries.length > 0)
            setSelectedHistoryIndex(i => Math.max(0, i - 1))
          break
        default: {
          const poi = adaptivePois.find(entry => 'key' in entry && entry.key === e.key.toLowerCase())
          if (poi) {
            flyToFn.current?.(poi.lat, poi.lon, poi.alt)
            setToast({ kind: 'info', title: `POI jump · ${poi.label}`, body: `${poi.note} · Rapid navigation hotkey ${poi.key.toUpperCase()}.` })
          }
          break
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [marketingRoute, activeCamera, activeFeedItem, activeVideo, historyMode, historyEntries, selectedHistoryIndex, adaptivePois])

  const flyTo = useCallback((lat: number, lon: number, altM = 1800000) => {
    flyToFn.current?.(lat, lon, altM)
  }, [])

  const trafficSimPoints = useMemo(() => {
    const cameraPoints = (cameras || []).filter(camera => {
      if (!viewBounds) return false
      return inBounds(camera.lat, camera.lon, viewBounds)
    }).slice(0, 12)
    return cameraPoints.map((camera, index) => {
      const roadwayBias = /i-\d+|us-\d+|sr-\d+|hwy|highway|bridge|tunnel|blvd|ave|road|rd/i.test(camera.name) ? 1 : 0.72
      const regionalBearing = camera.lon < -110 ? 92 : camera.lon < -90 ? 78 : camera.lon < -70 ? 64 : 48
      const statusBias = camera.hasLiveStream === false ? 0.68 : 1
      const intensity = Math.max(0.22, Math.min(1, (1 - index * 0.045) * roadwayBias * statusBias))
      return {
        id: `traffic-${camera.id}`,
        lat: camera.lat,
        lon: camera.lon,
        label: camera.name,
        intensity,
        heading: regionalBearing,
        laneCount: roadwayBias > 0.9 ? 3 : 2,
        corridor: camera.city || camera.state || 'traffic corridor',
      }
    })
  }, [cameras, viewBounds])

  const saveCurrentScene = useCallback(() => {
    if (!viewBounds) return
    const name = sceneName.trim() || `Scene ${savedScenes.length + 1}`
    const lat = (viewBounds.north + viewBounds.south) / 2
    const lon = (viewBounds.east + viewBounds.west) / 2
    const alt = Math.max(12000, cameraTelemetry.altitudeKm * 1000)
    const nextScene = {
      id: `scene-${Date.now()}`,
      name,
      lat,
      lon,
      alt,
      mode,
      mapType,
      effectStrength,
      timelineMinute,
      layers: { ...layers },
    }
    setSavedScenes(current => [nextScene, ...current].slice(0, 12))
    setSceneName('')
    apiFetch('/api/worldview/scenes', { method: 'POST', body: JSON.stringify(nextScene) }).catch(() => undefined)
    pushToast(`Scene saved: ${name}`, 'ok')
  }, [viewBounds, sceneName, savedScenes.length, cameraTelemetry.altitudeKm, mode, mapType, effectStrength, timelineMinute, layers, pushToast])

  const loadScene = useCallback((scene: { lat: number; lon: number; alt: number; mode: VisualMode; mapType: string; effectStrength: number; timelineMinute: number; layers: Record<LayerKey, boolean>; name: string }) => {
    setMode(scene.mode)
    setMapType(scene.mapType)
    setEffectStrength(scene.effectStrength)
    setTimelineMinute(scene.timelineMinute)
    setLayers(scene.layers)
    flyTo(scene.lat, scene.lon, scene.alt)
    pushToast(`Scene loaded: ${scene.name}`, 'info')
  }, [flyTo, pushToast])

  const deleteScene = useCallback((sceneId: string) => {
    setSavedScenes(current => current.filter(scene => scene.id !== sceneId))
    apiFetch(`/api/worldview/scenes/${encodeURIComponent(sceneId)}`, { method: 'DELETE' }).catch(() => undefined)
    pushToast('Scene deleted', 'info')
  }, [pushToast])

  const entityCollections = useMemo(() => {
    try {
      return !snapshot ? [] : [
        ...(Array.isArray(snapshot.events) ? snapshot.events : []).map(i => ({ entityType: 'event' as const, ...i })),
        ...(Array.isArray(snapshot.flights) ? snapshot.flights : []).map(i => ({ entityType: 'flight' as const, ...i })),
        ...(Array.isArray(snapshot.satellites) ? snapshot.satellites : []).map(i => ({ entityType: 'satellite' as const, ...i })),
        ...(Array.isArray(snapshot.maritime) ? snapshot.maritime : []).map(i => ({ entityType: 'maritime' as const, ...i })),
        ...(Array.isArray(snapshot.earthquakes) ? snapshot.earthquakes : []).map(i => ({ entityType: 'earthquake' as const, ...i })),
        ...(Array.isArray(snapshot.volcanoes) ? snapshot.volcanoes : []).map(i => ({ entityType: 'volcano' as const, ...i })),
        ...(Array.isArray(cameras) ? cameras : []).map(i => ({ entityType: 'camera' as const, ...i, id: `cam-${i.id}` })),
      ]
    } catch (error) {
      console.error('Entity collection derivation failed', error)
      return []
    }
  }, [snapshot, cameras])

  const entityById = useMemo(() => {
    const map = new Map<string, (typeof entityCollections)[number]>()
    entityCollections.forEach(entity => map.set(entity.id, entity))
    return map
  }, [entityCollections])

  const allEntities = entityCollections

  const visibleEntities = useMemo(() => allEntities.filter(e => {
    const lat = 'lat' in e && typeof (e as { lat?: unknown }).lat === 'number' ? (e as { lat: number }).lat : 0
    const lon = 'lon' in e && typeof (e as { lon?: unknown }).lon === 'number' ? (e as { lon: number }).lon : 0
    return inBounds(lat, lon, viewBounds)
  }), [allEntities, viewBounds])

  const selectedItem = useMemo(() => (selectedId ? entityById.get(selectedId) ?? null : null), [entityById, selectedId])

  const feed = useMemo(() => {
    try {
      const built = buildFeed(snapshot, historyMode === 'live' ? liveEventLog : [])
      return Array.isArray(built) ? built : []
    } catch (error) {
      console.error('Feed derivation failed', error)
      return []
    }
  }, [snapshot, historyMode, liveEventLog])

  const feedById = useMemo(() => {
    const map = new Map<string, (typeof feed)[number]>()
    feed.forEach(item => map.set(item.id, item))
    return map
  }, [feed])

  const visibleFeed = useMemo(() => feed.filter(f => inBounds(f.lat, f.lon, viewBounds)), [feed, viewBounds])
  const displayFeed = useMemo(() => (visibleFeed.length > 0 ? visibleFeed : feed), [visibleFeed, feed])
  const selectedHistoryEntry = useMemo(() => (Array.isArray(historyEntries) ? (historyEntries[selectedHistoryIndex] ?? null) : null), [historyEntries, selectedHistoryIndex])
  const liveSourceCount = useMemo(() => (Array.isArray(snapshot?.sourceStatus) ? snapshot.sourceStatus.filter(s => s.state === 'live').length : 0), [snapshot?.sourceStatus])
  const sourceStatusMap = useMemo(() => {
    const entries = Array.isArray(snapshot?.sourceStatus) ? snapshot.sourceStatus : []
    return new Map(entries.map(entry => [entry.name.toLowerCase(), entry]))
  }, [snapshot?.sourceStatus])

  const feedControlEntries = useMemo(() => {
    const statuses = Array.isArray(snapshot?.sourceStatus) ? snapshot.sourceStatus : []
    return sourceProviderMappings.map(mapping => {
      const sourceStatus = statuses.find(entry => mapping.matchers.some(matcher => entry.name.toLowerCase().includes(matcher)))
      return {
        ...mapping,
        sourceStatus,
      }
    })
  }, [snapshot?.sourceStatus])

  const liveTimelineSeries = useMemo(() => {
    const BUCKETS = 24
    const now = Date.now()
    const counts = new Array(BUCKETS).fill(0)
    displayFeed.forEach(item => {
      const t = new Date(item?.time ?? '').getTime()
      if (isNaN(t)) return
      const h = (now - t) / 3_600_000
      if (h >= 0 && h < BUCKETS) counts[BUCKETS - 1 - Math.floor(h)]++
    })
    const peak = Math.max(1, ...counts)
    return counts.map((c, i) => ({
      minute: i,
      baseline: Math.max(8, (c / peak) * 88),
      flightCount: Math.max(4, (c / peak) * 68),
      jamming: i === BUCKETS - 1 ? 40 : 5,
    }))
  }, [displayFeed])

  const stats = useMemo(() => {
    const flights = Array.isArray(snapshot?.flights) ? snapshot.flights : []
    const satellites = Array.isArray(snapshot?.satellites) ? snapshot.satellites : []
    const jamCells = Array.isArray(snapshot?.jamCells) ? snapshot.jamCells : []
    let militaryFlights = 0
    if (layers.flights) {
      for (const flight of flights) {
        if (flight && flight.type === 'military') militaryFlights += 1
      }
    }
    let severeJam = 0
    if (layers.jamming) {
      for (const cell of jamCells) {
        if (cell && typeof cell.intensity === 'number' && cell.intensity >= 0.7) severeJam += 1
      }
    }
    return {
      activeFlights: layers.flights ? flights.length : 0,
      militaryFlights,
      activeSats: layers.satellites ? satellites.length : 0,
      severeJam,
    }
  }, [snapshot, layers])

  const toastFeed = useMemo(() => (selectedId ? feedById.get(selectedId) ?? displayFeed[0] ?? null : displayFeed[0] ?? null), [selectedId, feedById, displayFeed])
  const classificationStyle = useMemo(() => {
    const lower = classificationBanner.toLowerCase()
    const bg = lower.includes('secret') ? '#700' : lower.includes('confid') ? '#740' : '#1a3d1a'
    const border = lower.includes('secret') ? '#a00' : lower.includes('confid') ? '#a60' : '#2d6b2d'
    return {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      background: bg,
      borderBottom: `1px solid ${border}`,
      color: '#fff',
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 700,
      fontFamily: 'monospace',
      letterSpacing: '0.15em',
      padding: '3px 0',
      userSelect: 'none',
    } as const
  }, [classificationBanner])

  const playbackSeries = useMemo(() => {
    if (historyMode !== 'history') return liveTimelineSeries
    return (Array.isArray(historyEntries) ? historyEntries : []).slice(0, 24).map((entry, index) => {
      const markerCount = typeof entry?.markerCount === 'number' && Number.isFinite(entry.markerCount) ? entry.markerCount : 0
      return {
        minute: index,
        baseline: Math.max(12, Math.min(88, 24 + markerCount * 9)),
        flightCount: selectedHistoryIndex === index ? 82 : Math.max(8, Math.min(68, 20 + markerCount * 6)),
        jamming: selectedHistoryIndex === index ? 54 : 8,
      }
    })
  }, [historyMode, historyEntries, selectedHistoryIndex, liveTimelineSeries])
  const globeHud = useMemo(() => (
    <div className="globe-hud worldview-globe-hud-enhanced">
      <div className="globe-hud-tl">
        <div className="hud-rec"><div className="hud-rec-dot" />REC {snapshot?.generatedAt ? new Date(snapshot.generatedAt).toISOString().slice(11, 19) : '--:--:--'} UTC</div>
        <div><span className="hud-label">MODE </span><span className="hud-value">{mode.toUpperCase()}</span></div>
        <div><span className="hud-label">SRC </span><span className="hud-value">{liveSourceCount} LIVE</span></div>
      </div>
      <div className="globe-hud-br">
        <div><span className="hud-label">HDG </span><span className="hud-value">{cameraTelemetry.headingDeg.toFixed(0)}°</span></div>
        <div><span className="hud-label">PITCH </span><span className="hud-value">{cameraTelemetry.pitchDeg.toFixed(0)}°</span></div>
        <div><span className="hud-label">ALT </span><span className="hud-value">{cameraTelemetry.altitudeKm.toFixed(0)} km</span></div>
        <div><span className="hud-label">ROLL </span><span className="hud-value">{cameraTelemetry.rollDeg.toFixed(1)}°</span></div>
      </div>
      <div className="globe-hud-bl worldview-hud-block">
        <div><span className="hud-label">EVENTS </span><span className="hud-value">{displayFeed.length}</span></div>
        <div><span className="hud-label">FLIGHTS </span><span className="hud-value">{stats.activeFlights}</span></div>
        <div><span className="hud-label">SATS </span><span className="hud-value">{stats.activeSats}</span></div>
      </div>
      <div className="globe-hud-tr worldview-hud-block">
        <div style={{ color: 'rgba(255,80,80,0.7)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em' }}>
          {stats.severeJam > 0 ? `⚠ JAMMING × ${stats.severeJam}` : 'SPECTRUM CLR'}
        </div>
        <div><span className="hud-label">DEFCON </span><span className="hud-value">4</span></div>
        <div><span className="hud-label">T+ </span><span className="hud-value">{String(timelineMinute).padStart(2, '0')}min</span></div>
      </div>
    </div>
  ), [snapshot?.generatedAt, mode, liveSourceCount, cameraTelemetry, displayFeed.length, stats, timelineMinute])

  const handleEntityClick = useCallback((id: string) => {
    if (typeof id !== 'string' || !id) return
    if (id.startsWith('cam-')) {
      const camId = id.replace('cam-', '')
      const cam = (Array.isArray(cameras) ? cameras : []).find(c => c.id === camId)
      if (cam) { setActiveCamera(cam); return }
    }
    setSelectedId(id)
    const match = entityById.get(id)
    if (match && match.entityType === 'event') {
      const eventMatch = feedById.get(id)
      if (eventMatch) setActiveFeedItem(eventMatch)
    }
  }, [entityById, cameras, feedById])

  const analyzeWithOllama = useCallback(async () => {
    if (ollamaStatus !== 'online') return
    setOllamaLoading(true)
    try {
      const summary = (Array.isArray(displayFeed) ? displayFeed : []).slice(0, 10).map(f => `[${f.category}] ${f.title}: ${f.body}`).join('\n')
      const prompt = `You are a geospatial intelligence analyst. Analyze these recent global events and identify patterns, emerging threats, or notable correlations. Be concise (3-4 sentences max):\n\n${summary}`
      const status = await apiFetch('/api/llm/status').then(r => r.json())
      if (!status.enabled || !status.endpoint) throw new Error('LLM disabled or not configured')
      const r = await fetch(`${status.endpoint}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: status.model || 'llama3.2', prompt, stream: false }),
        signal: AbortSignal.timeout(40000),
      })
      const data: { response?: string } = await r.json()
      setOllamaAnalysis(data.response ?? 'No analysis returned.')
    } catch (e) {
      setOllamaAnalysis(`Error: ${e instanceof Error ? e.message : 'Request failed'}`)
    } finally {
      setOllamaLoading(false)
    }
  }, [ollamaStatus, displayFeed])

  const sendAssistantMessage = useCallback(async (message: string, intent: 'chat' | 'analyze-screen' = 'chat') => {
    const trimmed = message.trim()
    if (!trimmed) return
    setAssistantMessages(prev => [...prev, { role: 'user', text: trimmed }])
    setAssistantInput('')
    setAssistantLoading(true)
    try {
      const liveSourceCount = (Array.isArray(snapshot?.sourceStatus) ? snapshot.sourceStatus : []).filter(s => s.state === 'live').length
      const context = `tab=${activeTab}; mode=${mode}; selectedId=${selectedId}; viewFilteredEvents=${Array.isArray(displayFeed) ? displayFeed.length : 0}; liveSources=${liveSourceCount}`
      const screen = {
        activeTab,
        mode,
        selectedId,
        leftRailCollapsed,
        stats,
        selectedItem,
        visibleFeedCount: Array.isArray(visibleFeed) ? visibleFeed.length : 0,
        displayFeed: (Array.isArray(displayFeed) ? displayFeed : []).slice(0, 12),
        sourceStatus: Array.isArray(snapshot?.sourceStatus) ? snapshot.sourceStatus : [],
      }
      const response = await apiFetch('/api/assistant/chat', {
        method: 'POST',
        body: JSON.stringify({ message: trimmed, context, screen, intent }),
      })
      const data = await response.json()
      setAssistantMessages(prev => [...prev, { role: 'assistant', text: data.response || 'No response returned.' }])
    } catch (err) {
      setAssistantMessages(prev => [...prev, { role: 'assistant', text: `Assistant error: ${err instanceof Error ? err.message : 'Request failed'}` }])
    } finally {
      setAssistantLoading(false)
    }
  }, [activeTab, mode, selectedId, displayFeed, snapshot, leftRailCollapsed, stats, selectedItem, visibleFeed])

  if (marketingRoute === 'landing') return <MarketingLanding />
  if (marketingRoute === 'login') return <MarketingLogin />

  const licensedModules = new Set(licenseStatus?.entitlement?.modules || [])
  const messagingEnabled = licensedModules.size === 0 || licensedModules.has('messaging')
  const adminEnabled = true
  const worldviewEnabled = licensedModules.size === 0 || licensedModules.has('worldview')

  return (
    <div className={cls('worldview-app', `mode-${mode}`)}>
      {showClassification && (
        <div style={classificationStyle}>
          {classificationBanner.toUpperCase()}
        </div>
      )}

      <div className="topbar" style={showClassification ? { marginTop: 26 } : undefined}>
        <div className="topbar-brand-lockup">
          <DecipherLogo />
        </div>
        <div className="topbar-actions">
          <div className="tab-switcher">
            <button className={cls('tab-btn', activeTab === 'worldview' && 'active')} onClick={() => worldviewEnabled && setActiveTab('worldview')} disabled={!worldviewEnabled}>
              <Globe2 size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />WorldView
            </button>
            <button className={cls('tab-btn', activeTab === 'admin' && 'active')} onClick={() => adminEnabled && setActiveTab('admin')} disabled={!adminEnabled}>
              <Settings size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />Admin
            </button>
          </div>
          <button className="pill" onClick={() => setAssistantOpen(true)}>
            <Bot size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Assistant
          </button>
          <button
            className="pill"
            title="Sign out"
            onClick={() => {
              localStorage.removeItem('godseye_token')
              localStorage.removeItem('godseye_user')
              sessionStorage.removeItem('godseye_token')
              sessionStorage.removeItem('godseye_user')
              navigateTo('/login')
            }}
            style={{ color: 'var(--muted)', borderColor: 'rgba(255,102,0,0.15)' }}
          >
            <LockKeyhole size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />Sign out
          </button>
        </div>
      </div>

      {activeTab === 'messaging' && messagingEnabled && (
        <Suspense fallback={<div className="note-card" style={{ margin: 24 }}>Loading messaging…</div>}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MessagingView />
          </div>
        </Suspense>
      )}
      {activeTab === 'admin' && (
        <Suspense fallback={<div className="note-card" style={{ margin: 24 }}>Loading admin controls…</div>}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <AdminPanel onToast={pushToast} />
          </div>
        </Suspense>
      )}

      {assistantOpen && (
        <div className="assistant-shell">
          <div className="assistant-backdrop" onClick={() => setAssistantOpen(false)} />
          <aside className="assistant-panel panel">
            <div className="assistant-header">
              <div>
                <div className="eyebrow"><Sparkles size={12} /> Mission Copilot</div>
                <h3>GODSEYE Assistant</h3>
              </div>
              <button className="rdrawer-close" onClick={() => setAssistantOpen(false)}><X size={14} /></button>
            </div>
            <div className="assistant-quick-actions">
              <button className="pill" onClick={() => void sendAssistantMessage('Analyze the current screen and summarize the most important risks, anomalies, and operator actions.', 'analyze-screen')}>Analyze screen</button>
              <button className="pill" onClick={() => void sendAssistantMessage('What settings should I adjust right now for this current operating state?', 'chat')}>Recommend settings</button>
            </div>
            <div className="assistant-messages">
              {assistantMessages.map((msg, idx) => (
                <div key={idx} className={cls('assistant-msg', msg.role === 'user' && 'user')}>
                  <div className="assistant-msg-role">{msg.role === 'user' ? 'You' : 'Assistant'}</div>
                  <div>{msg.text}</div>
                </div>
              ))}
              {assistantLoading && <div className="assistant-msg"><div className="assistant-msg-role">Assistant</div><div>Thinking…</div></div>}
            </div>
            <div className="assistant-input-row">
              <textarea
                className="assistant-input"
                value={assistantInput}
                onChange={e => setAssistantInput(e.target.value)}
                placeholder="Ask for analysis, control guidance, or safe setting changes…"
                rows={3}
              />
              <button className="pill emphasis" onClick={() => void sendAssistantMessage(assistantInput)} disabled={assistantLoading || !assistantInput.trim()}>
                <Send size={14} style={{ marginRight: 6 }} />Send
              </button>
            </div>
          </aside>
        </div>
      )}

      {!worldviewEnabled && (
        <div style={{ padding: 24, margin: 24, border: '1px solid rgba(255,102,0,0.2)', borderRadius: 16, background: 'rgba(11,18,27,0.92)', color: '#d0e4f0' }}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#c9a227', marginBottom: 10 }}>License Required</div>
          <h2 style={{ margin: '0 0 8px' }}>WorldView is not enabled for this deployment</h2>
          <p style={{ margin: 0, color: '#89a3b8' }}>
            Activate a valid license or update entitlements in the Admin Licensing tab to unlock this module.
          </p>
        </div>
      )}

      {activeTab === 'worldview' && worldviewEnabled && (
        <div className={cls('shell-grid', leftRailCollapsed && 'shell-grid-left-collapsed')}>
          <aside className={cls('left-rail', 'panel', leftRailCollapsed && 'left-rail-collapsed')}>
            <div className="left-rail-toggle-wrap">
              <button
                className="left-rail-toggle"
                onClick={() => setLeftRailCollapsed(v => !v)}
                title={leftRailCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={leftRailCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {leftRailCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
            </div>

            {!leftRailCollapsed && visibleSections.includes('controls') ? (
              <div className="worldview-minimal-command-rail">
                <div style={{ fontSize: 10, color: 'rgba(190,220,240,0.68)', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '2px 2px 0' }}>Command Rail</div>
                <button className={cls('worldview-command-chip', commandPanel === 'layers' && 'active')} onClick={() => setCommandPanel(current => current === 'layers' ? null : 'layers')}><Layers3 size={15} /> Controls</button>
                <button className={cls('worldview-command-chip', commandPanel === 'feeds' && 'active')} onClick={() => setCommandPanel(current => current === 'feeds' ? null : 'feeds')}><Activity size={15} /> Live Feeds</button>
                <button className={cls('worldview-command-chip', commandPanel === 'scenes' && 'active')} onClick={() => setCommandPanel(current => current === 'scenes' ? null : 'scenes')}><Satellite size={15} /> Scenes</button>
                <button className={cls('worldview-command-chip', commandPanel === 'media' && 'active')} onClick={() => setCommandPanel(current => current === 'media' ? null : 'media')}><Tv size={15} /> Live Media</button>
                <div className="worldview-command-status-block">
                  <div className="panel-header spaced"><Radar size={16} /><span>Source Health</span></div>
                  <div className="source-list worldview-source-list-compact">
                    {(snapshot?.sourceStatus ?? []).slice(0, 5).map((s, index) => (
                      <div key={s.name} className="source-row worldview-source-row-compact">
                        <span className={cls('source-dot', sourceDot(s.state))} />
                        <span>{s.name}</span>
                        <span className="source-signal" aria-label={`${s.name} ${s.state} activity`}>
                          {[0, 1, 2, 3].map(bar => (
                            <span
                              key={`${s.name}-${bar}`}
                              className={cls('source-signal-bar', `source-signal-${s.state}`)}
                              style={{
                                animationDelay: `${index * 120 + bar * 90}ms`,
                                animationDuration: `${Math.max(0.45, 1.9 - ((s.activityLevel ?? 0.2) * 1.2) + bar * 0.06)}s`,
                                opacity: Math.max(0.2, Math.min(1, (s.activityLevel ?? 0.2) * (0.72 + bar * 0.08))),
                              }}
                            />
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="left-rail-collapsed-icons">
                <button className="collapsed-icon-chip active" onClick={() => setLeftRailCollapsed(false)} title="Layer Stack"><Layers3 size={16} /></button>
                <button className="collapsed-icon-chip" onClick={() => setLeftRailCollapsed(false)} title="Source Health"><Radar size={16} /></button>
                <button className="collapsed-icon-chip" onClick={() => setLeftRailCollapsed(false)} title="Source Health"><Radar size={16} /></button>
                <button className="collapsed-icon-chip" onClick={() => setLeftRailCollapsed(false)} title="GodsEye AI"><Zap size={16} /></button>
                <button className="collapsed-icon-chip" onClick={() => setLeftRailCollapsed(false)} title="Analyst Notes"><ShieldAlert size={16} /></button>
              </div>
            )}
          </aside>

          <main className="center-stage worldview-main-stage">
            <ViewErrorBoundary label="Worldview Main Stage">
            <section className="globe-shell panel worldview-command-shell">
              <div className="globe-header worldview-globe-header-compact">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="worldview-title-block worldview-title-block-compact">
                    <div className="eyebrow">WORLDVIEW · LIVE GLOBAL OPERATING PICTURE</div>
                    <h2 style={{ margin: 0, fontFamily: 'monospace', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Mission Intelligence Feed</h2>
                  </div>
                </div>
                <div className="globe-legend worldview-top-legend" style={{ opacity: 0.72 }}>
                  <span><i className="legend flight" /> Flights</span>
                  <span><i className="legend sat" /> Satellites</span>
                  <span><i className="legend maritime" /> Maritime</span>
                  <span><i className="legend jam" /> GPS Jam</span>
                  <span><i className="legend event" /> Events</span>
                  <span><i className="legend news" /> News</span>
                </div>
              </div>

              <div className="worldview-globe-stage">
                <div className="worldview-globe-frame worldview-globe-frame-outer" />
                <div className="worldview-globe-frame worldview-globe-frame-inner" />
                <div className="worldview-globe-top-band">
                  <span>GLOBAL WATCH</span>
                  <span>{liveSourceCount} LIVE SOURCES</span>
                  <span>{displayFeed.length} EVENTS</span>
                  <span>HDG {cameraTelemetry.headingDeg.toFixed(0)}°</span>
                  <span>ALT {cameraTelemetry.altitudeKm.toFixed(0)}KM</span>
                </div>
                <div className="worldview-scope-nav worldview-scope-nav-left">PAN {cameraTelemetry.headingDeg.toFixed(1)}° / TILT {cameraTelemetry.pitchDeg.toFixed(1)}°</div>
                <div className="worldview-scope-nav worldview-scope-nav-right">ROLL {cameraTelemetry.rollDeg.toFixed(1)}° · ZOOM {cameraTelemetry.altitudeKm.toFixed(0)}KM</div>
                <div style={{ position: 'absolute', top: 52, left: 18, zIndex: 4, display: 'grid', gap: 10, maxWidth: 620 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {adaptivePois.map(poi => (
                      <button
                        key={poi.id}
                        className="button"
                        style={{ fontSize: 10, padding: '6px 10px', background: 'rgba(6,14,24,0.82)', borderColor: 'rgba(70,110,140,0.45)' }}
                        onClick={() => {
                          flyTo(poi.lat, poi.lon, poi.alt)
                          pushToast(`POI jump: ${poi.label}`, 'info')
                        }}
                        title={poi.note}
                      >
                        {'key' in poi ? `${poi.key.toUpperCase()} · ` : ''}{poi.label}
                      </button>
                    ))}
                  </div>

                  {commandPanel && (
                    <div className="worldview-command-popup panel">
                      <div className="worldview-command-popup-header">
                        <div className="eyebrow">Command Surface</div>
                        <button className="rdrawer-close" onClick={() => setCommandPanel(null)}><X size={14} /></button>
                      </div>

                      {commandPanel === 'layers' && (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div className="panel-header"><Layers3 size={16} /><span>Mission Controls</span></div>
                          <div className="worldview-slim-layer-list">
                            {(Object.keys(layerLabels) as LayerKey[]).map(key => (
                              <button
                                key={key}
                                className={cls('worldview-slim-layer-row', layers[key] && 'active')}
                                onClick={() => handleToggleLayer(key)}
                              >
                                <span>{layerLabels[key]}</span>
                                <strong>{layers[key] ? 'ON' : 'OFF'}</strong>
                              </button>
                            ))}
                          </div>
                          <div className="worldview-rail-section worldview-ai-brief" style={{ marginTop: 4 }}>
                            <div className="panel-header spaced">
                              <span style={{ fontSize: 14 }}>🧠</span>
                              <span>AI Brief</span>
                              <span
                                style={{
                                  marginLeft: 'auto', fontSize: 10, borderRadius: 4, padding: '1px 6px',
                                  background: ollamaStatus === 'online' ? '#1a3d1a' : '#2a1a1a',
                                  color: ollamaStatus === 'online' ? '#44ff88' : '#ff5555',
                                  border: `1px solid ${ollamaStatus === 'online' ? '#2d6b2d' : '#4d2222'}`,
                                }}
                              >
                                {ollamaStatus === 'online' ? 'ONLINE' : ollamaStatus === 'offline' ? 'OFFLINE' : '…'}
                              </span>
                            </div>
                            <div style={{ padding: '4px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {ollamaStatus === 'online' ? (
                                <button className="button" style={{ width: '100%' }} onClick={() => void analyzeWithOllama()} disabled={ollamaLoading}>
                                  {ollamaLoading ? 'Analyzing…' : 'Analyze'}
                                </button>
                              ) : (
                                <div className="note-card worldview-compact-note" style={{ fontSize: 10 }}>Ollama offline.</div>
                              )}
                              {ollamaAnalysis && (
                                <div style={{ fontSize: 11, lineHeight: 1.6, color: '#a8c0d0', padding: '8px 10px', background: '#0d1520', borderRadius: 6, border: '1px solid #1e3044' }}>
                                  {ollamaAnalysis}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {commandPanel === 'feeds' && (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div className="panel-header"><Activity size={16} /><span>Live Feeds & Inputs</span></div>
                          <div className="note-card worldview-compact-note" style={{ fontSize: 11 }}>
                            Use this popup to control the live inputs feeding the operating picture. Close it to return to a compressed, map-first interface.
                          </div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={() => applySourcePreset('air-picture')}>Air picture</button>
                              <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={() => applySourcePreset('urban-traffic')}>Urban traffic</button>
                              <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={() => applySourcePreset('news-narrative')}>News narrative</button>
                              <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={() => applySourcePreset('full-fusion')}>Full fusion</button>
                            </div>
                            <div style={{ display: 'grid', gap: 6, padding: '10px', borderRadius: 8, border: '1px solid rgba(70,110,140,0.18)', background: 'rgba(7,11,16,0.72)' }}>
                              <div style={{ fontSize: 10, color: 'rgba(190,220,240,0.78)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Visible dashboard sections</div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {['controls', 'feeds', 'scenes', 'media', 'pressure', 'intel-rail'].map(section => (
                                  <button
                                    key={section}
                                    className="button"
                                    style={{ fontSize: 10, padding: '5px 9px', background: visibleSections.includes(section) ? 'rgba(255,102,0,0.14)' : 'rgba(11,19,29,0.62)' }}
                                    onClick={() => {
                                      const isVisible = visibleSections.includes(section)
                                      if (isVisible && visibleSections.length <= 1) {
                                        pushToast('At least one dashboard section must remain visible', 'warn')
                                        return
                                      }
                                      const nextSections = isVisible
                                        ? visibleSections.filter(entry => entry !== section)
                                        : [...visibleSections, section]
                                      setVisibleSections(nextSections)
                                      apiFetch('/api/worldview/workspace', { method: 'POST', body: JSON.stringify({ visibleSections: nextSections, defaultPresetId }) }).catch(() => undefined)
                                    }}
                                  >
                                    {section}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <input
                                value={presetName}
                                onChange={(event) => setPresetName(event.target.value)}
                                placeholder="Operator preset name"
                                style={{ minWidth: 180, background: 'rgba(6,14,24,0.92)', color: '#d5e7f5', border: '1px solid rgba(70,110,140,0.45)', borderRadius: 6, padding: '7px 10px' }}
                              />
                              <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={saveOperatorPreset}>Save preset</button>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {savedPresets.slice(0, 4).map(preset => (
                                <div key={preset.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  {editingPresetId === preset.id ? (
                                    <input
                                      value={editingPresetName}
                                      onChange={(event) => setEditingPresetName(event.target.value)}
                                      onBlur={() => renameOperatorPreset(preset.id, editingPresetName)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') renameOperatorPreset(preset.id, editingPresetName)
                                        if (event.key === 'Escape') {
                                          setEditingPresetId(null)
                                          setEditingPresetName('')
                                        }
                                      }}
                                      autoFocus
                                      style={{ minWidth: 130, fontSize: 10, background: 'rgba(6,14,24,0.92)', color: '#d5e7f5', border: '1px solid rgba(70,110,140,0.45)', borderRadius: 6, padding: '6px 8px' }}
                                    />
                                  ) : (
                                    <button className="button" style={{ fontSize: 10, padding: '5px 9px', background: preset.id === defaultPresetId ? 'rgba(255,102,0,0.14)' : 'rgba(11,19,29,0.62)' }} onClick={() => {
                                      applySavedPreset(preset)
                                      setDefaultPresetId(preset.id)
                                      apiFetch('/api/worldview/workspace', { method: 'POST', body: JSON.stringify({ visibleSections: preset.visibleSections ?? visibleSections, defaultPresetId: preset.id }) }).catch(() => undefined)
                                    }}>
                                      {preset.name}
                                    </button>
                                  )}
                                  <button className="button" style={{ fontSize: 10, padding: '5px 8px' }} onClick={() => {
                                    setEditingPresetId(preset.id)
                                    setEditingPresetName(preset.name)
                                  }}>
                                    Rename
                                  </button>
                                  <button className="button" style={{ fontSize: 10, padding: '5px 8px', background: 'rgba(40,10,10,0.72)', borderColor: 'rgba(140,70,70,0.4)' }} onClick={() => deleteOperatorPreset(preset.id)}>
                                    Delete
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {feedControlEntries.map(feed => {
                              const sourceStatus = feed.sourceStatus
                              const status = feed.layer === 'cameras'
                                ? (layers[feed.layer] ? `${cameras.length} LOADED` : 'LAYER OFF')
                                : feed.layer === 'news'
                                  ? `${newsFeeds.length} CONFIGURED`
                                  : (layers[feed.layer] ? 'ACTIVE IN VIEW' : 'LAYER OFF')
                              const detail = feed.key === 'opensky'
                                ? 'Primary live aircraft input for civil and military flight overlays.'
                                : feed.key === 'adsb'
                                  ? 'Aircraft telemetry and track inputs used in the global flight picture.'
                                  : feed.key === 'cameras'
                                    ? 'Traffic and scenic camera network feeding live visual context and inferred traffic simulation.'
                                    : 'Broadcaster feeds available for popup viewing and narrative context.'
                              return (
                              <div key={feed.title} style={{ display: 'grid', gap: 6, padding: '10px', borderRadius: 8, border: '1px solid rgba(70,110,140,0.18)', background: 'rgba(7,11,16,0.72)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                  <strong style={{ fontSize: 12, color: '#d8e7f5' }}>{feed.title}</strong>
                                  <span style={{ fontSize: 10, color: '#7fb1d1' }}>{status}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <span className={cls('source-dot', sourceDot(sourceStatus?.state || 'standby'))} />
                                  <span style={{ fontSize: 10, color: '#b5cede' }}>{sourceStatus?.name || 'Derived feed control'}</span>
                                  <span style={{ fontSize: 10, color: '#7fb1d1' }}>{(sourceStatus?.state || 'standby').toUpperCase()}</span>
                                  <span style={{ fontSize: 10, color: 'rgba(190,220,240,0.68)' }}>activity {(sourceStatus?.activityLevel ?? 0).toFixed(2)}</span>
                                </div>
                                <div style={{ fontSize: 11, color: '#89a3b8' }}>{detail}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <button className="button" style={{ fontSize: 10, padding: '5px 9px' }} onClick={() => handleToggleLayer(feed.layer)}>
                                    {layers[feed.layer] ? 'Disable layer' : 'Enable layer'}
                                  </button>
                                  <button className="button" style={{ fontSize: 10, padding: '5px 9px', background: 'rgba(11,19,29,0.62)' }} onClick={() => {
                                    if (!layers[feed.layer]) handleToggleLayer(feed.layer)
                                    pushToast(`${feed.title} prioritized`, 'info')
                                  }}>
                                    Prioritize
                                  </button>
                                  <span style={{ fontSize: 10, color: 'rgba(190,220,240,0.68)' }}>Layer control inside feed popup</span>
                                </div>
                              </div>
                            )})}
                          </div>
                        </div>
                      )}

                      {commandPanel === 'scenes' && (
                        <div style={{ display: 'grid', gap: 8, alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input
                              value={sceneName}
                              onChange={(event) => setSceneName(event.target.value)}
                              placeholder="Scene name"
                              style={{ minWidth: 180, background: 'rgba(6,14,24,0.92)', color: '#d5e7f5', border: '1px solid rgba(70,110,140,0.45)', borderRadius: 6, padding: '7px 10px' }}
                            />
                            <button className="button" style={{ fontSize: 10, padding: '6px 10px' }} onClick={saveCurrentScene}>Save scene</button>
                            <span style={{ fontSize: 10, color: 'rgba(190,220,240,0.78)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Scene bank</span>
                          </div>
                          <div style={{ display: 'grid', gap: 6 }}>
                            {savedScenes.slice(0, 8).map(scene => (
                              <div key={scene.id} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', background: 'rgba(11,19,29,0.88)', border: '1px solid rgba(70,110,140,0.18)', borderRadius: 8, padding: '6px 8px' }}>
                                <div style={{ display: 'grid', gap: 2 }}>
                                  <button className="button" style={{ fontSize: 10, padding: '6px 10px', background: 'rgba(11,19,29,0.12)', textAlign: 'left' }} onClick={() => loadScene(scene)}>
                                    {scene.name}
                                  </button>
                                  <div style={{ fontSize: 10, color: 'rgba(190,220,240,0.68)' }}>
                                    {scene.mode.toUpperCase()} · {scene.mapType} · T+{String(scene.timelineMinute).padStart(2, '0')} · ALT {(scene.alt / 1000).toFixed(0)}KM
                                  </div>
                                </div>
                                <button className="button" style={{ fontSize: 10, padding: '6px 10px', background: 'rgba(40,10,10,0.72)', borderColor: 'rgba(140,70,70,0.4)' }} onClick={() => deleteScene(scene.id)}>
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {commandPanel === 'media' && (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div className="panel-header"><Tv size={16} /><span>Live News Media</span></div>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {newsFeeds.map((feed, index) => (
                              <button
                                key={feed.id || `${feed.name}-${index}`}
                                className="worldview-slim-layer-row"
                                onClick={() => setSelectedNewsFeed(feed)}
                                style={{ textAlign: 'left' }}
                              >
                                <span>{feed.name}</span>
                                <strong>OPEN</strong>
                              </button>
                            ))}
                            {newsFeeds.length === 0 && (
                              <div className="note-card worldview-compact-note" style={{ fontSize: 10 }}>
                                No live news feeds configured yet.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <GlobePanelLazy
                  snapshot={snapshot}
                  layers={layers}
                  mode={mode}
                  effectStrength={effectStrength}
                  mapType={mapType}
                  timelineMinute={timelineMinute}
                  cameras={cameras}
                  trafficSimPoints={trafficSimPoints}
                  onViewChange={handleViewChange}
                  onCameraTelemetryChange={handleCameraTelemetryChange}
                  onFlyToReady={handleFlyToReady}
                  onEntityClick={handleEntityClick}
                  overlayHud={globeHud}
                />
              </div>

              {visibleSections.includes('pressure') && (
              <WorldviewTimelineStrip
                cls={cls}
                apiFetch={apiFetch as unknown as typeof fetch}
                viewBounds={viewBounds}
                pressureSeries={pressureSeries}
                pressureLoading={pressureLoading}
                pressureError={pressureError}
                pressureDetail={pressureDetail}
                activePressureCategory={activePressureCategory}
                visiblePressureCategories={visiblePressureCategories}
                pressureRegionMode={pressureRegionMode}
                pressureRadiusKm={pressureRadiusKm}
                pressurePresets={pressurePresets}
                activePressurePresetId={activePressurePresetId}
                setActivePressureCategory={setActivePressureCategory}
                setVisiblePressureCategories={setVisiblePressureCategories}
                setPressureRegionMode={setPressureRegionMode}
                setPressureRadiusKm={setPressureRadiusKm}
                setPressureDetail={setPressureDetail}
                historyMode={historyMode}
                setHistoryMode={setHistoryMode}
                historyPlaying={historyPlaying}
                setHistoryPlaying={setHistoryPlaying}
                historyEntries={historyEntries}
                selectedHistoryIndex={selectedHistoryIndex}
                setSelectedHistoryIndex={setSelectedHistoryIndex}
                setTimelineMinute={setTimelineMinute}
                selectedHistoryEntry={selectedHistoryEntry}
                historyLoading={historyLoading}
                visibleFeed={visibleFeed}
                displayFeed={displayFeed}
                layerLabels={layerLabels}
                layers={layers}
                setLayers={setLayers}
                stockTicker={stockTicker}
                setActiveStock={setActiveStock}
              />
              )}
            </section>

            {visibleSections.includes('media') && selectedNewsFeed && (
              <div className="assistant-shell">
                <div className="assistant-backdrop" onClick={() => setSelectedNewsFeed(null)} />
                <aside className="assistant-panel panel" style={{ width: 'min(860px, calc(100vw - 32px))', height: 'min(78vh, calc(100vh - 40px))' }}>
                  <div className="assistant-header">
                    <div>
                      <div className="eyebrow"><Tv size={12} /> Live News Media</div>
                      <h3>{selectedNewsFeed.name}</h3>
                    </div>
                    <button className="rdrawer-close" onClick={() => setSelectedNewsFeed(null)}><X size={14} /></button>
                  </div>
                  <div className="news-drawer-player-shell" style={{ minHeight: 0, flex: 1, borderRadius: 10, overflow: 'hidden' }}>
                    {newsDrawersReady ? (
                      <iframe className="news-drawer-player" src={selectedNewsFeed.resolvedUrl || selectedNewsFeed.url} title={`${selectedNewsFeed.resolvedTitle || selectedNewsFeed.name} live feed`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                    ) : (
                      <div className="news-drawer-empty">
                        <strong>Preparing live embed…</strong>
                        <span>Initial UI render is prioritized; the feed mounts after the command surface settles.</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: '#89a3b8' }}>Select a network from Live Media and open it in this popup player.</div>
                    <a href={selectedNewsFeed.resolvedWatchUrl || selectedNewsFeed.url} target="_blank" rel="noreferrer" className="news-drawer-link">Open source</a>
                  </div>
                </aside>
              </div>
            )}
            {activeStock && (
              <div className="note-card" style={{ marginTop: 12, position: 'relative' }}>
                <button onClick={() => setActiveStock(null)} style={{ position: 'absolute', top: 10, right: 10, border: 'none', background: 'transparent', color: '#89a3b8', cursor: 'pointer' }}>✕</button>
                <div className="eyebrow">Market Intelligence</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <strong style={{ fontSize: 18 }}>{activeStock.symbol}</strong>
                  <div style={{ fontFamily: 'monospace', color: '#d7e7f3' }}>{typeof activeStock.price === 'number' && Number.isFinite(activeStock.price) && activeStock.price > 0 ? `$${activeStock.price.toFixed(2)}` : 'PRIVATE'} · {typeof activeStock.change === 'number' && activeStock.change >= 0 ? '+' : ''}{typeof activeStock.change === 'number' && Number.isFinite(activeStock.change) ? activeStock.change.toFixed(2) : '?.??'}%</div>
                </div>
                <div style={{ marginTop: 8, color: '#c8d8e8' }}>{activeStock.headline}</div>
                <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                  {(Array.isArray(activeStock.news) ? activeStock.news : []).map((item: string, idx: number) => <div key={idx} style={{ color: '#89a3b8', fontSize: 12 }}>• {item}</div>)}
                </div>
              </div>
            )}
            {error && <div className="note-card" style={{ marginTop: 12 }}>Feed error: {error}</div>}
            </ViewErrorBoundary>
          </main>

          {visibleSections.includes('intel-rail') && (
          <WorldviewTelemetryRail
            cls={cls}
            fmtTime={fmtTime}
            CATEGORY_ICON={CATEGORY_ICON}
            snapshot={snapshot}
            viewBounds={viewBounds}
            visibleFeed={visibleFeed}
            displayFeed={displayFeed}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            setActiveFeedItem={setActiveFeedItem}
            activeFeedItem={activeFeedItem}
            flyTo={flyTo}
            onSelectFeedItem={handleSelectFeedItem}
            setActiveVideo={setActiveVideo}
            pressureSeries={pressureSeries}
            pressureDetail={pressureDetail}
            activePressureCategory={activePressureCategory}
            ViewErrorBoundary={ViewErrorBoundary}
          />
          )}
        </div>
      )}

      {activeTab === 'worldview' && toastFeed && !toastDismissed && (
        <div className="toast panel">
          <button className="toast-close" onClick={() => setToastDismissed(true)} aria-label="Dismiss"><X size={14} /></button>
          <div className="eyebrow">{CATEGORY_ICON[toastFeed.category] ?? '·'} {toastFeed.category}</div>
          <strong>{toastFeed.title}</strong>
          <div className="muted">{toastFeed.body}</div>
          {toastFeed.time && <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4 }}>{fmtTime(toastFeed.time)}</div>}
          <button className="button" style={{ marginTop: 8, width: '100%', fontSize: 11 }} onClick={() => { flyTo(toastFeed.lat, toastFeed.lon, 1800000); setToastDismissed(true) }}>🎯 Fly to event</button>
        </div>
      )}

      <WorldviewSelectedItemModal
        activeTab={activeTab}
        selectedItem={selectedItem}
        activeFeedItem={activeFeedItem}
        onClose={() => setSelectedId(null)}
        flyTo={flyTo}
      />

      {activeVideo && (
        <div className="rdrawer-backdrop" onClick={() => setActiveVideo(null)}>
          <div className="rdrawer" style={{ width: 'min(1100px, 94vw)', height: 'min(760px, 86vh)', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }} onClick={e => e.stopPropagation()}>
            <div className="rdrawer-titlebar">
              <span className="rdrawer-title">Video Player · {activeVideo.title}</span>
              <button className="rdrawer-close" onClick={() => setActiveVideo(null)}>×</button>
            </div>
            <div className="rdrawer-body" style={{ padding: 14 }}>
              {activeVideo.embedUrl ? (
                <iframe
                  className="in-app-video-frame"
                  src={activeVideo.embedUrl}
                  title={activeVideo.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <div className="note-card">This video does not expose an embeddable player. Use the source link instead.</div>
              )}
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{activeVideo.title}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{activeVideo.channelTitle || activeVideo.source || 'YouTube'}</div>
                </div>
                <a href={activeVideo.url} target="_blank" rel="noreferrer" className="news-drawer-link">Open in YouTube</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeCamera && <CameraPopup camera={activeCamera} onClose={() => setActiveCamera(null)} />}

      {/* Worldview toast notifications */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 28, right: 24, zIndex: 99999,
          display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
          pointerEvents: 'none',
        }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              padding: '9px 16px', borderRadius: 5, fontSize: 12,
              fontFamily: 'Courier New, monospace', letterSpacing: '.04em',
              maxWidth: 380, pointerEvents: 'auto',
              background: t.kind === 'ok'  ? 'rgba(0,232,122,0.12)'
                        : t.kind === 'err' ? 'rgba(255,51,68,0.12)'
                        : 'rgba(255,106,0,0.12)',
              border: `1px solid ${t.kind === 'ok' ? 'rgba(0,232,122,0.4)' : t.kind === 'err' ? 'rgba(255,51,68,0.4)' : 'rgba(255,106,0,0.4)'}`,
              color:  t.kind === 'ok'  ? '#00e87a'
                    : t.kind === 'err' ? '#ff3344'
                    : '#ff9a44',
              boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
              animation: 'fadeSlideIn .2s ease',
            }}>
              {t.kind === 'ok' ? '✓ ' : t.kind === 'err' ? '✗ ' : '· '}{t.text}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
